import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { handleEnterPress } from '../../shared/helpers/util';
import { getContactNumber } from '../../shared/services/contactsService';
import { ApiCall } from '../../shared/types/Call';
import { ApiPhoneExtension } from '../../shared/types/PhoneExtension';
import { ApiPhonebookEntry } from '../../shared/types/PhonebookEntry';
import time from '../helpers/time';
import { startCall } from '../reducers/calls';
import {
  combinedContactsSelector,
  combinedEndpointsSelector,
  contactNamesByNumberSelector,
} from '../selectors/contacts';
import { StoreDispatch, StoreState } from '../store';
import * as styles from '../styles/historicCall.module.scss';
import StatusLine from './StatusLine';
import { InboundCallIcon, OutboundCallIcon } from './icons';

interface Props {
  call: ApiCall;
}

const HistoricCall: React.FC<Props> = ({ call }) => {
  const dispatch = useDispatch<StoreDispatch>();
  const endpoints = useSelector(combinedEndpointsSelector);
  const contactsMap = useSelector(contactNamesByNumberSelector);
  const allContacts = useSelector(combinedContactsSelector);
  const hasGrantedAudio = useSelector(
    (state: StoreState) => state.audio.hasGrantedAudio,
  );
  const {
    scope,
    direction,
    to: callTo,
    callerId,
    callStarted,
    outcome,
    duration,
    dstEndpoint,
  } = call;

  // TODO Add the rest of the statuses
  // Answered = ANSWERED
  // Missed = NO_ANSWER, BUSY
  // Failed = FAILED

  // const answered = outcome === 'ANSWERED';
  const failed = outcome === 'FAILED';

  const title = failed ? 'Call Failed' : '';
  const directionIcon =
    direction === 'IN' ? <InboundCallIcon /> : <OutboundCallIcon />;

  const name = direction === 'IN' ? callerId : callTo;

  let lookedUpName = '';
  let description = '';

  if (scope === 'LOCAL' && direction === 'OUT') {
    // Find the destination in the store of endpoints
    const destinationEndpoint = endpoints.find((e) => {
      return e.id === dstEndpoint;
    });
    if (destinationEndpoint) {
      // If we find it, set the details as they are on that endpoint
      lookedUpName = destinationEndpoint.name;
      description = destinationEndpoint.shortNumber;
    } else {
      // If all else fails split the string in the 'to' field
      // into name and shortNumber and display those
      const splitTo = /(.*)\s<(\d{3,4})>/gi.exec(callTo);
      const endpointName = splitTo?.[1];
      const endpointShortNumber = splitTo?.[2];
      lookedUpName = endpointName || '';
      description = endpointShortNumber || '';
    }
  } else if (contactsMap) {
    // Look up name in contacts map
    lookedUpName = contactsMap[name];
    // If we find it, set the description as the original number
    description = lookedUpName ? name : '';
  }

  // TODO if callerId and from are different (incoming call) display hidden 'via' option
  // eg call from 01234567890 via Main IVR <301>
  // if (callerId !== callFrom) {
  //   const via = callFrom;
  // }

  const sendCall = (number: string) => {
    // If there's no number, don't bother
    if (number.length === 0) {
      return;
    }

    // Lookup entered info to see if it matches any known contact
    // If it does, initiate the call with more detailed info
    const found = allContacts.some((c) => {
      const contactNumber = getContactNumber(c);
      if (contactNumber === number) {
        dispatch(
          startCall({
            number: contactNumber,
            type: c.type,
            name: c.name,
            description: contactNumber,
            email: (c as ApiPhoneExtension | ApiPhonebookEntry).email,
          }),
        );
        return true;
      }
      return false;
    });

    // If not, just send a basic call
    if (!found) {
      dispatch(startCall({ number }));
    }
  };

  const onClick = () => {
    const number = description || name;
    sendCall(number);
  };
  return (
    <li
      className={`${styles.container} ${!hasGrantedAudio && styles.disabled}`}
      onClick={onClick}
      onKeyDown={handleEnterPress(onClick)}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role='button'
      tabIndex={0}
    >
      <StatusLine
        text={time.formatTime(callStarted)}
        icon={directionIcon}
        error={failed}
        title={title}
      />
      <div className={styles.text_container}>
        <div className={styles.detail_container}>
          <div>{lookedUpName || name || 'Anonymous'}</div>
          {description ? (
            <div className={styles.description}>{description}</div>
          ) : (
            ''
          )}
        </div>
        <div className={styles.time_container}>{time.duration(duration)}</div>
      </div>
    </li>
  );
};

export default HistoricCall;
